import { isEmpty } from "lodash";
import { useJsApiLoader } from "@react-google-maps/api";
import { types, utils } from "@vilocnv/allsetra-core";

const GOOGLE_MAPS_LIBRARIES = ["drawing"] as any[];

export const useGoogleMapsLoader = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`,
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: "nl"
  });

  return { isLoaded, loadError };
};

function latRad(lat: any) {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
}

function zoom(mapPx: any, worldPx: any, fraction: any) {
  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
}

export function getBoundsZoomLevel(
  coords: Array<{ latitude: number; longitude: number }>,
  mapDim: { height: number; width: number }
) {
  try {
    const bounds = new google.maps.LatLngBounds();

    coords.map((coord) =>
      bounds.extend({ lat: coord.latitude, lng: coord.longitude })
    );

    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  } catch (e: any) {
    console.log("getBoundsZoomLevel error", e.message);
  }
}

export const extractObjectsDataAsMarkersForMap = (
  objects: Array<types.IObject>
) => {
  if (isEmpty(objects)) return [];

  return objects
    .filter(
      (item: any) =>
        item.location &&
        item.location.latitude !== null &&
        item.location.longitude !== null &&
        item.location.latitude !== 0 &&
        item.location.longitude !== 0
    )
    .map((item: any) => {
      const { latitude, longitude } = item?.location;
      const iconUrl = item?.objectType?.icon?.url || item?.icon?.url;

      const originalDate = new Date(item.location.date);
      const formattedDate = originalDate.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: false
      });

      return {
        //due to icons displacement issue
        lat: latitude - 0.00001,
        lng: longitude,
        iconUrl,
        name: item?.name,
        aNumber: item?.aNumber,
        accuracy: item?.location.accuracy,
        formattedDate,
        lastReportedSpeed: item?.lastReportedSpeed,
        lastReportedDirection: item?.lastReportedDirection,
        direction: item?.direction,
        uniqueId: item?.uniqueId,
        resolvedAddress: item?.location?.resolvedAddress || "N/A"
      };
    });
};

export const getAddressFromLatLng = (lat: number, lng: number) => {
  return fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`
  )
    .then((response) => response.json())
    .then((data) => {
      if (data?.status === "OK") {
        const address = data?.results[0]?.formatted_address;
        return address;
      } else {
        throw new Error("Geocoding failed " + data.status);
      }
    })
    .catch((error) => {
      console.error("Error fetching address ", error.message);
    });
};
